import React, { useMemo, useState, useEffect } from 'react';
import { parse } from 'date-fns';

import DatePicker from 'components/DatePicker';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import { store } from 'redux/store';
import { setFilter } from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import usePeriodType from 'hooks/Meteorology/Fields/ModelPerformance/usePeriodType';
import useOptions from 'hooks/Meteorology/Fields/ModelPerformance/useOptions';
import useSelectedRounds from 'hooks/Meteorology/Fields/ModelPerformance/useSelectedRounds';

export default function FreeRoundsSection({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const { selectedRounds, setSelectedRounds } = useSelectedRounds();
  const { periodType } = usePeriodType();
  const { availableRounds, minDate, maxDate } = useOptions();

  const [localStartDate, setLocalStartDate] = useState<Date | null>(null);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(null);

  const hasRounds = availableRounds != null && availableRounds.length > 0;

  const minDateObj = useMemo(
    () => (minDate ? parse(minDate, 'yyyy-MM-dd', new Date()) : undefined),
    [minDate],
  );
  const maxDateObj = useMemo(
    () => (maxDate ? parse(maxDate, 'yyyy-MM-dd', new Date()) : undefined),
    [maxDate],
  );

  useEffect(() => {
    if (periodType !== 'free') {
      setLocalStartDate(null);
      setLocalEndDate(null);
    }
  }, [periodType]);

  useEffect(() => {
    if (selectedRounds.length === 0) return;
    const times = selectedRounds.map((d) => d.getTime());
    setLocalStartDate(new Date(Math.min(...times)));
    setLocalEndDate(new Date(Math.max(...times)));
  }, [selectedRounds]);

  const startDate = useMemo(() => {
    if (localStartDate) return localStartDate;
    if (!selectedRounds.length) return null;
    return new Date(Math.min(...selectedRounds.map((d) => d.getTime())));
  }, [localStartDate, selectedRounds]);

  const endDate = useMemo(() => {
    if (localEndDate) return localEndDate;
    if (!selectedRounds.length) return null;
    return new Date(Math.max(...selectedRounds.map((d) => d.getTime())));
  }, [localEndDate, selectedRounds]);

  const handleDateChange = (dates: (Date | null)[] | Date | null) => {
    store.dispatch(setFilter({ analysisPeriod: undefined }));
    if (!Array.isArray(dates)) {
      setLocalStartDate(null);
      setLocalEndDate(null);
      setSelectedRounds([]);
      return;
    }
    const [newStartDate, newEndDate] = dates;
    setLocalStartDate(newStartDate);
    setLocalEndDate(newEndDate);

    if (newStartDate && newEndDate) {
      const startTime = newStartDate.getTime();
      const endTime = newEndDate.getTime();
      const inRange = (availableRounds ?? [])
        .map((roundStr) => parse(roundStr, 'yyyy-MM-dd', new Date()))
        .filter((d) => d.getTime() >= startTime && d.getTime() <= endTime);
      setSelectedRounds(inRange);
    } else {
      setSelectedRounds([]);
    }
  };

  const valueText = selectedRounds.length > 0 ? `${selectedRounds.length} rodada(s)` : undefined;

  return (
    <Section label="Selecione as rodadas" valueText={valueText} open={open} onToggle={onToggle}>
      {!hasRounds && (
        <p className="mp-mobile-sheet-hint">Selecione o tipo de análise e a variável primeiro</p>
      )}
      {hasRounds && (
        <DatePicker
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
          minDate={minDateObj}
          maxDate={maxDateObj}
          inline
          containerClass="ds-scroll-datepicker"
        />
      )}
    </Section>
  );
}
